import { useEffect, useRef } from "react";
import Capture from "../utils/Capture";

type useProctoringCaptureProps = {
  enabled: boolean;
  allowCapture: boolean;
  id: string;
  hmacSecurityKey: string;
  assessment: string;
  endpoint: string;
  audioCapture?: boolean;
  screenCapture?: boolean;
};

export default function useProctoringCapture({
  enabled,
  allowCapture,
  id,
  hmacSecurityKey,
  assessment,
  endpoint,
  audioCapture = false,
  screenCapture = false
}: useProctoringCaptureProps): void {
  const captureInstanceRef = useRef<Capture | null>(null);
  const isMobile = useRef<boolean>(
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  );

  useEffect(() => {
    if (!enabled || !id || !endpoint || !assessment) return;

    const proctoringSettings = {
      enabled,
      audioCapture,
      screenCapture: screenCapture && !isMobile.current
    };

    captureInstanceRef.current = new Capture(
      assessment,
      id,
      endpoint,
      proctoringSettings,
      hmacSecurityKey,
      isMobile.current
    );

    captureInstanceRef.current.enable();

    // If screen capture is enabled, we'll need user interaction - safari causes issues
    if (screenCapture && !isMobile.current) {
      const handleClick = () => {
        if (captureInstanceRef.current) {
          if (window.screenShareRequestActive) {
            window.screenShareRequestActive(true);
          }

          captureInstanceRef.current.enableScreenCapture(true).finally(() => {
            if (window.screenShareRequestActive) {
              setTimeout(() => {
                window.screenShareRequestActive?.(false);
              }, 500);
            }
          });
        }
      };
      window.addEventListener("click", handleClick, { once: true });
      return () => {
        window.removeEventListener("click", handleClick);
      };
    }

    return () => {
      if (captureInstanceRef.current) {
        captureInstanceRef.current.disable();
        captureInstanceRef.current = null;
      }
    };
  }, [
    enabled,
    id,
    endpoint,
    assessment,
    hmacSecurityKey,
    audioCapture,
    screenCapture
  ]);

  useEffect(() => {
    if (!enabled || !captureInstanceRef.current) return;

    captureInstanceRef.current.toggleCapture(allowCapture);
  }, [enabled, allowCapture]);
}
